"use client";

import Image from "next/image";
import { Play, Eye, Twitter, Linkedin, Youtube, ChevronLeft, ChevronRight } from "lucide-react";
import { useState, useRef } from "react";

interface FeaturedVideoProps {
  isMobile: boolean;
}

interface VideoData {
  id: string;
  title: string;
  views: string;
  duration: string;
}

const videos: VideoData[] = [
  {
    id: "3wglqgskzjQ",
    title: "Anthropic killed Tool calling",
    views: "203K+",
    duration: "14:09",
  },
  {
    id: "YJ3Z9XhlF5w",
    title: "Claude Designer is insane...Ultimate vibe coding UI workflow",
    views: "189K+",
    duration: "16:38",
  },
  {
    id: "UZb0if-7wGE",
    title: "I was using Claude Code wrong... The Ultimate Workflow",
    views: "140K+",
    duration: "18:44",
  },
  {
    id: "LCYBVpSB0Wo",
    title: "I was using sub-agents wrong... Here is my way after 20+ hrs test",
    views: "116K+",
    duration: "16:01",
  },
  {
    id: "RYnxU_MTVvU",
    title: "Nano Banana + Gemini 3 = S-TIER UI DESIGNER",
    views: "98K+",
    duration: "11:28",
  },
  {
    id: "kJPvfoLtFFY",
    title: "wtf is Harness Engineer & why is it important",
    views: "66K+",
    duration: "15:16",
  },
];

function VideoCard({ video }: { video: VideoData }) {
  const [playing, setPlaying] = useState(false);
  const thumbnailUrl = `https://img.youtube.com/vi/${video.id}/mqdefault.jpg`;

  return (
    <div className="group flex-shrink-0 w-[260px] sm:w-[280px]">
      <div className="relative aspect-video rounded-lg overflow-hidden border border-white/10 mb-2.5">
        {playing ? (
          <iframe
            src={`https://www.youtube.com/embed/${video.id}?autoplay=1&rel=0`}
            title={video.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
          />
        ) : (
          <button
            onClick={() => setPlaying(true)}
            className="absolute inset-0 w-full h-full cursor-pointer"
          >
            <Image
              src={thumbnailUrl}
              alt={video.title}
              fill
              className="object-cover"
            />
            <div className="absolute inset-0 bg-black/20 group-hover:bg-black/0 transition-colors" />
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                <Play className="h-4 w-4 text-black ml-0.5" fill="black" />
              </div>
            </div>
            <div className="absolute bottom-1.5 right-1.5 bg-black/80 text-white text-[11px] px-1.5 py-0.5 rounded font-medium">
              {video.duration}
            </div>
          </button>
        )}
      </div>
      <h4 className="text-[13px] font-medium leading-snug text-white/90 line-clamp-2 mb-1">
        {video.title}
      </h4>
      <div className="flex items-center gap-1.5 text-[11px] text-white/40">
        <Eye className="h-3 w-3" />
        <span>{video.views} views</span>
      </div>
    </div>
  );
}

function VideoCarousel({ videos }: { videos: VideoData[] }) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: "left" | "right") => {
    if (!scrollRef.current) return;
    const amount = 300;
    scrollRef.current.scrollBy({
      left: direction === "left" ? -amount : amount,
      behavior: "smooth",
    });
  };

  return (
    <div className="relative">
      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto scrollbar-hide pb-2"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {videos.map((video) => (
          <VideoCard key={video.id} video={video} />
        ))}
      </div>
      <button
        onClick={() => scroll("left")}
        className="absolute left-0 top-[25%] -translate-y-1/2 -translate-x-3 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors backdrop-blur-sm hidden lg:flex"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <button
        onClick={() => scroll("right")}
        className="absolute right-0 top-[25%] -translate-y-1/2 translate-x-3 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors backdrop-blur-sm hidden lg:flex"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function FeaturedVideo({ isMobile }: FeaturedVideoProps) {
  return (
    <section className="bg-black text-white pt-16 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs uppercase tracking-widest text-white/30 font-medium mb-10">
          The founder
        </p>

        {/* Founder bio - aihero.dev style */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center mb-20">
          {/* Portrait */}
          <div className="relative w-full max-w-[260px] sm:max-w-md mx-auto lg:mx-0 aspect-square rounded-2xl overflow-hidden">
            <Image
              src="/jason-zhou.png"
              alt="Jason Zhou"
              fill
              className="object-cover"
              priority
            />
          </div>

          {/* Bio text */}
          <div>
            <h3 className="text-3xl sm:text-4xl font-bold mb-6">
              Hi, I&apos;m Jason Zhou
            </h3>

            <div className="space-y-4 text-[15px] sm:text-base text-white/55 leading-relaxed">
              <p>
                I&apos;m a VC-backed startup founder, and I&apos;ve been building
                AI products since before it was cool - 3+ years shipping
                production AI agents.
              </p>
              <p>
                Before this I was Head of Product at Relevance AI. I also run a
                YouTube channel where I reverse-engineer how tools like Claude
                Code, Cursor, and Manus actually work under the hood - 8M+ views
                so far.
              </p>
              <p>
                I started AI Builder Club because I was tired of seeing builders
                waste months on hype instead of shipping. Everything I teach
                comes from stuff I&apos;ve built and shipped myself.
              </p>
            </div>

            <div className="flex items-center gap-4 mt-8">
              <a
                href="https://x.com/jasonzhou1993"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
                aria-label="Twitter"
              >
                <Twitter className="h-4 w-4 text-white/50" />
              </a>
              <a
                href="https://www.linkedin.com/in/jasonzhou1993"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="h-4 w-4 text-white/50" />
              </a>
              <a
                href="https://www.youtube.com/@AIJasonZ"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
                aria-label="YouTube"
              >
                <Youtube className="h-4 w-4 text-white/50" />
              </a>
            </div>
          </div>
        </div>

        {/* Video carousel */}
        <div className="w-full min-w-0">
          <p className="text-xs uppercase tracking-widest text-white/30 font-medium mb-5">
            Popular videos
          </p>
          <VideoCarousel videos={videos} />
        </div>
      </div>
    </section>
  );
}
